import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import "../styles/checkout.css";

import VoucherModal from "../components/VoucherModal";
import PaymentModal from "../components/PaymentModal";
import { UserContext } from "../UserContext";

/* =====================================================
   Configuration
===================================================== */

// Explicit backend base (never localhost:3000)
const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5000";

// DEV detection rule (shared across app)
const isDevEmail = (email) =>
  typeof email === "string" && email.endsWith("@dev.edgeneweb");

/* =====================================================
   Plans Data
===================================================== */
const checkoutData = {
  bundledPlans: [
    {
      title: "Gold User",
      description:
        "Full access to STEM Subjects and Venting Room services.",
      price: "R230 pm",
      features: ["STEM Subjects", "Venting Room"],
      id: "gold",
      paystackAmount: 23000,
    },
    {
      title: "Platinum User",
      description:
        "Unlimited access to STEM Subjects, Venting Room, and Entrepreneur services.",
      price: "R570 pm",
      features: [
        "STEM Subjects",
        "Venting Room",
        "Entrepreneurship",
      ],
      id: "platinum",
      paystackAmount: 57000,
    },
  ],
  individualPlans: [
    {
      title: "Venting Room User",
      description:
        "Post & receive support for various life challenges.",
      price: "R50 pm",
      features: ["Venting Room"],
      id: "venting",
      paystackAmount: 5000,
    },
    {
      title: "STEM Subject Student",
      description:
        "Enroll in STEM tutoring and access interactive learning resources.",
      price: "R200 pm",
      features: ["STEM Subjects"],
      id: "stem",
      paystackAmount: 20000,
    },
    {
      title: "Entrepreneur Candidate",
      description:
        "Join entrepreneurship education programs.",
      price: "R350 pm",
      features: ["Entrepreneurship"],
      id: "entrepreneur",
      paystackAmount: 35000,
    },
  ],
};

const Checkout = () => {
  const navigate = useNavigate();
  const {
    userEmail,
    userName,
    userSurname,
    isAuthenticated,
    login,
  } = useContext(UserContext);

  const [showVoucherModal, setShowVoucherModal] =
    useState(false);
  const [showPaymentModal, setShowPaymentModal] =
    useState(false);
  const [voucherCount, setVoucherCount] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [isBundled, setIsBundled] = useState(true);
  const [plans, setPlans] = useState(
    checkoutData.bundledPlans
  );
  const [currentSlide, setCurrentSlide] = useState(0);
  const [transitioning, setTransitioning] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /* =====================================================
     Auth Guard
  ===================================================== */
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  /* =====================================================
     Fetch Voucher Count (Safe)
  ===================================================== */
  useEffect(() => {
    // DEV shortcut
    if (isDevEmail(userEmail)) {
      setVoucherCount(999);
      return;
    }

    const fetchVoucherCount = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/api/vouchers/remaining`
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const text = await res.text();
        if (!text.startsWith("{")) {
          throw new Error(
            "Invalid response (expected JSON)"
          );
        }

        const data = JSON.parse(text);
        setVoucherCount(data.remaining ?? 0);
      } catch (err) {
        console.error(
          "Error fetching voucher count:",
          err
        );
        setVoucherCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVoucherCount();
  }, [userEmail]);

  /* =====================================================
     Slider Logic
  ===================================================== */
  const nextSlide = () => {
    if (!transitioning) {
      setTransitioning(true);
      setCurrentSlide(
        (prev) => (prev + 1) % plans.length
      );
    }
  };

  const prevSlide = () => {
    if (!transitioning) {
      setTransitioning(true);
      setCurrentSlide(
        (prev) =>
          (prev - 1 + plans.length) % plans.length
      );
    }
  };

  useEffect(() => {
    const t = setTimeout(
      () => setTransitioning(false),
      300
    );
    return () => clearTimeout(t);
  }, [currentSlide, plans]);

  /* =====================================================
     Plan Selection
  ===================================================== */
  const handlePlanTypeChange = (bundled) => {
    if (transitioning) return;

    setTransitioning(true);
    setIsBundled(bundled);
    setPlans(
      bundled
        ? checkoutData.bundledPlans
        : checkoutData.individualPlans
    );
    setCurrentSlide(0);
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);

    if (!isBundled && voucherCount > 0) {
      setShowVoucherModal(true);
    } else {
      setShowPaymentModal(true);
    }
  };

  /* =====================================================
     ✅ PAYMENT SUCCESS HANDLER (CORE LOGIC)
  ===================================================== */
  const handlePaymentComplete = () => {
    // Persist payment info for dashboards
    localStorage.setItem("paymentStatus", "success");
    localStorage.setItem(
      "paidPlan",
      JSON.stringify(selectedPlan)
    );

    // Update context (subscription activated)
    login(
      userEmail,
      userName,
      userSurname,
      selectedPlan.id,
      false
    );

    // ✅ Redirect ONLY for Entrepreneur plan
    if (selectedPlan.id === "entrepreneur") {
      navigate(
        "/entrepreneurship-education-dashboard"
      );
      return;
    }

    // Default routing for other plans
    switch (selectedPlan.id) {
      case "venting":
        navigate("/dev-venting-room-dashboard");
        break;
      case "stem":
        window.location.href =
          "https://edgene.vercel.app/";
        break;
      default:
        navigate("/dashboard");
    }
  };

  /* =====================================================
     Voucher Callbacks
  ===================================================== */
  const handleVoucherUsed = () => {
    setShowVoucherModal(false);
    handlePaymentComplete();
  };

  /* =====================================================
     Render
  ===================================================== */
  return (
    <div className="checkout-container">
      {showVoucherModal && (
        <VoucherModal
          isOpen={showVoucherModal}
          onClose={() => setShowVoucherModal(false)}
          plan={selectedPlan}
          onVoucherUsed={handleVoucherUsed}
          userEmail={userEmail}
          onContinueToPayment={() =>
            setShowPaymentModal(true)
          }
        />
      )}

      {showPaymentModal && selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          plan={selectedPlan}
          userEmail={userEmail}
          onPaymentComplete={handlePaymentComplete}
        />
      )}

      <div className="checkout">
        <h1>Checkout</h1>

        {!isBundled && voucherCount > 0 && (
          <p className="voucher-info">
            Free-month vouchers remaining:{" "}
            {voucherCount}
          </p>
        )}

        <div className="plan-type-toggle">
          <button
            className={isBundled ? "active" : ""}
            onClick={() =>
              handlePlanTypeChange(true)
            }
          >
            Bundled Plans
          </button>
          <button
            className={!isBundled ? "active" : ""}
            onClick={() =>
              handlePlanTypeChange(false)
            }
          >
            Individual Plans
          </button>
        </div>

        <div className="slideshow-container">
          <div
            className="slides"
            style={{
              transform: `translateX(-${
                currentSlide * 100
              }%)`,
            }}
          >
            {plans.map((plan, index) => (
              <div key={index} className="slide">
                <div className="plan-card">
                  <h3>{plan.title}</h3>
                  <p>{plan.description}</p>
                  <p className="price">
                    {plan.price}
                  </p>
                  <button
                    onClick={() =>
                      handleSelectPlan(plan)
                    }
                    disabled={isLoading}
                  >
                    Select Plan
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            className="prev-button"
            onClick={prevSlide}
          >
            <FaAngleLeft />
          </button>
          <button
            className="next-button"
            onClick={nextSlide}
          >
            <FaAngleRight />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
